/**
 * Turns stored rows into the §11.2 snapshot fields that describe history.
 *
 * §11.2's warning is the reason this file exists: the danger in leaving these
 * derivations out of the core "is not a wrong dose but the repair an
 * implementer reaches for — computing provenance in the shell at render time,
 * which puts safety-display logic outside the tested core."
 *
 * Every function is pure and takes `nowMs` as data (§7.6).
 */

import {
  BAND_E_FULL_CARD_WINDOW_HOURS,
  CLOCK_SKEW_TOLERANCE_HOURS,
  KETONE_ADVISORY,
  MS_PER_HOUR,
} from '../config.js';
import { deriveCarbBaseline } from './baseline.js';
import type { InsulinClass } from './insulin.js';
import { isInjection } from './types.js';
import type { HistoryProvenance, Injection, LastDose, LogRow, Reading } from './types.js';

const SKEW_TOLERANCE_MS = CLOCK_SKEW_TOLERANCE_HOURS * MS_PER_HOUR;

/** §7.6 — below -1 h elapsed is implausibly future-dated. */
export function isImplausiblyFutureDated(timestamp: number, nowMs: number): boolean {
  return nowMs - timestamp < -SKEW_TOLERANCE_MS;
}

/**
 * §10.5, and it reads BOTH STORES — the contradiction resolved in v10.
 *
 * v9 specified this twice and incompatibly: §10.5's formula said log-only,
 * while §7.8, §4.3 step 1 and two §13.3 cases said both stores. That is
 * contradictory oracles for the same required test — a standalone 280 reading
 * at breakfast followed by the day's first calculated result at lunch demands
 * FULL under one and COMPACT under the other — in the one warning this plan
 * calls capable of catching ketoacidosis.
 *
 * A ROLLING WINDOW, not a calendar day. Ruled by Momin 2026-09-13, and
 * `BACKLOG.md`'s "Band E's full card resets on a rolling window, not a
 * calendar day" carries the argument. The old boundary was local midnight and
 * it failed at both ends — 23:40 and 00:20 are one episode and got two full
 * cards, 03:00 and 21:00 are two episodes and the second got a compact line.
 *
 * The rename came with the ruling: this used to be `...ShownToday`, which
 * stopped being true the moment the boundary stopped being a date.
 *
 * **It no longer takes a time zone.** A duration is zone-free by construction,
 * so this function's dependence on notes 11 and 21's device-zone ruling is
 * gone — one fewer thing that can be wrong on a travelling phone.
 *
 * @param excludeId §10.5 v11 — the window is evaluated EXCLUDING the row being
 *   committed, or the breakfast 280 renders compact for itself.
 */
export function deriveBandEFullCardShownRecently(
  rows: readonly LogRow[],
  readings: readonly Reading[],
  nowMs: number,
  excludeId?: string,
): boolean {
  // Inclusive at the edge and skew-bounded above, matching `hasRowInsideWindow`
  // exactly. The future bound is not decoration: the old day-key test excluded
  // a row dated three days ahead for free, and `elapsed < window` alone would
  // admit one dated any distance into the future. §7.6 already owns that
  // judgement, so it is reused rather than restated.
  const cutoff = nowMs - BAND_E_FULL_CARD_WINDOW_HOURS * MS_PER_HOUR;
  const insideWindow = (timestamp: number): boolean =>
    timestamp >= cutoff && !isImplausiblyFutureDated(timestamp, nowMs);

  // Stryker disable next-line MethodExpression: removing `.filter(isInjection)`
  // changes no result. `LogRow` is `Injection | Tombstone`, a tombstone carries
  // no `bloodSugar`, and `undefined >= 250` is false — so a tombstone cannot
  // qualify with or without the filter. The type system needs it regardless.
  // Same equivalence, and the same reasoning, as `baseline.ts`'s tombstone
  // filter.
  //
  // SURFACED by narrowing the block disable that used to wrap this expression:
  // that block silenced 31 mutants under a justification about the null check
  // three lines down, and this one — equivalent for an entirely different
  // reason — was riding along unexamined. Which is the argument against block
  // disables in one example.
  const qualifyingInLog = rows.filter(isInjection).some(
    (row) =>
      row.id !== excludeId &&
      // Stryker disable next-line EqualityOperator,ConditionalExpression: this
      // null check changes no RESULT, because `null >= 250` coerces to
      // `0 >= 250`. It stays because that coercion is the one §4.1 exists to
      // refuse — a rule that holds only where it happens to be observable is
      // not a rule.
      //
      // NARROWED 2026-09-11. A `disable all` block wrapped this whole
      // expression and silenced 31 mutants on one line's worth of reasoning,
      // including the `row.id !== excludeId` mutant that §13.3's "the breakfast
      // 280 renders compact for itself" case exists to kill. Nothing was hiding
      // under it — re-enabling proved every neighbour dies — but a block-wide
      // disable wearing a one-mutant justification is how a real gap would hide.
      row.bloodSugar !== null &&
      row.bloodSugar >= KETONE_ADVISORY &&
      insideWindow(row.timestamp),
  );
  if (qualifyingInLog) return true;

  // §10.5's second ruling, forced by "both stores": recording a reading at or
  // above 250 shows the band E advisory ITSELF. Otherwise the first calculated
  // result inside the window could render compact when no full card was ever
  // shown, and this flag would assert something false.
  return readings.some(
    (reading) =>
      reading.id !== excludeId &&
      reading.bloodSugar >= KETONE_ADVISORY &&
      insideWindow(reading.timestamp),
  );
}

export interface DerivedHistory {
  readonly lastDose: LastDose | null;
  readonly historyProvenance: HistoryProvenance;
  readonly excludedTimeRecords: number;
  readonly carbBaseline: number | null;
  readonly eligibleEntryCount: number;
}

export interface HistoryContext {
  readonly nowMs: number;
  /** When this install first ran. A log whose newest row predates it is suspect. */
  readonly installedAtMs: number;
  /** When history was last imported, or null if it never was. */
  readonly lastImportAtMs: number | null;
  /** When this install last wrote a row itself, or null. */
  readonly lastLocalInjectionAtMs: number | null;
  /**
   * §11.3 — how many stored rows failed re-validation on load and were dropped.
   * A log the app had to prune is a log it cannot vouch for, so this joins
   * §7.5's suspect conditions: dropping a row must never silently become the
   * assertion that there was no recent insulin, which is the exact class §7.5
   * exists to refuse.
   */
  readonly droppedStoredRows: number;
  /**
   * §8.5 — which insulin class each prescription period was, so the gate can
   * key on the insulin that produced the last dose rather than on the one
   * selected since.
   *
   * Passed as DATA rather than looked up, for the reason §13.1 gives about the
   * clock: a core module that reached into the settings store to answer this
   * would be a core module with a dependency the golden cases cannot control.
   */
  readonly classByRevision: ReadonlyMap<number, InsulinClass | null>;
}

function mostRecentUsableInjection(
  rows: readonly LogRow[],
  nowMs: number,
): Injection | null {
  let best: Injection | null = null;
  for (const row of rows) {
    if (!isInjection(row)) continue;
    if (isImplausiblyFutureDated(row.timestamp, nowMs)) continue;
    if (best === null || row.timestamp > best.timestamp) best = row;
  }
  return best;
}

/**
 * §7.5 — "no usable recent record" must never silently assert "no recent
 * insulin". A backup from yesterday passes every schema, type and range check
 * while omitting an injection from an hour ago.
 *
 * The five suspect conditions §7.5 names: an empty log, a log predating the
 * app's own install, history just imported, a row excluded by §7.6, or a row
 * DROPPED by §11.3's load-time re-validation. The fifth was added on
 * 2026-09-11, when the load path started re-validating at all — it is the same
 * claim as the others, that a log the app had to prune is not one it can
 * vouch for. §7.5 names it as the fifth condition, amended 2026-09-11.
 */
export function deriveHistory(
  rows: readonly LogRow[],
  context: HistoryContext,
): DerivedHistory {
  const injections = rows.filter(isInjection);
  const excludedTimeRecords = injections.filter((row) =>
    isImplausiblyFutureDated(row.timestamp, context.nowMs),
  ).length;

  const newest = mostRecentUsableInjection(rows, context.nowMs);
  const lastDose: LastDose | null =
    newest === null
      ? null
      : {
          injectedHundredths: newest.injectedUnits,
          atMs: newest.timestamp,
          // §8.5's switch-day rule — the insulin THIS row was given under,
          // read through the revision that stamped it. Never the insulin in
          // force now: the point of the rule is the case where those differ,
          // and taking today's answer would make the window shrink under a
          // dose of yesterday's insulin that is still acting.
          //
          // `?? null` covers a revision with no class recorded, which
          // `windowsFor` answers with the longest windows in the table.
          insulinClass: context.classByRevision.get(newest.settingsRevision) ?? null,
        };

  const logIsEmpty = injections.length === 0;
  const predatesInstall = newest !== null && newest.timestamp < context.installedAtMs;
  const justImported =
    context.lastImportAtMs !== null &&
    // Stryker disable next-line ConditionalExpression,EqualityOperator: the
    // `=== null` half is not separately observable — `null < someTimestamp`
    // coerces to `0 < timestamp`, true for every real clock value, so the
    // comparison alone gives the same answer. Written out because §4.1 forbids
    // relying on that coercion.
    //
    // NARROWED 2026-09-11 with the sibling block above. The whole expression was
    // silenced under this one clause's reasoning, which hid that every OTHER
    // mutant here is killable: `lastImportAtMs !== null`, the `&&`, the `||` and
    // the `<` are all pinned by `test/history.test.ts`.
    (context.lastLocalInjectionAtMs === null ||
      context.lastLocalInjectionAtMs < context.lastImportAtMs);

  const historyProvenance: HistoryProvenance =
    logIsEmpty ||
    predatesInstall ||
    justImported ||
    excludedTimeRecords > 0 ||
    context.droppedStoredRows > 0
      ? 'suspect'
      : 'trusted';

  const { carbBaseline, eligibleEntryCount } = deriveCarbBaseline(rows);

  return { lastDose, historyProvenance, excludedTimeRecords, carbBaseline, eligibleEntryCount };
}

/**
 * §7.3 — every count the user is shown excludes tombstones. He deleted those
 * rows already, and §7.9's "delete 67 entries" must not count them.
 */
export function countDisplayableRows(rows: readonly LogRow[]): number {
  return rows.filter(isInjection).length;
}

/** §7.9 — is there a row inside the delete-confirmation window? */
export function hasRowInsideWindow(
  rows: readonly LogRow[],
  nowMs: number,
  windowHours: number,
): boolean {
  const cutoff = nowMs - windowHours * MS_PER_HOUR;
  return rows
    .filter(isInjection)
    .some((row) => row.timestamp >= cutoff && !isImplausiblyFutureDated(row.timestamp, nowMs));
}
